// The room's walls as something a product can meet (WallFacts), searched by
// the face and corner snaps and face alignment alongside products, as
// SurfaceFacts, by the same rules. Built from all the walls together, its
// faces are the room's interior faces only: the floor plan is cut into a
// grid at every wall's edges, the cells the walls close in are the room, and
// a face lies wherever a room cell meets a wall, merged along its run. So
// faces are cut off at the room's inside corners and joined across its
// outside corners, as at the inside of an L, with no wall ends or outer
// sides. Its solid boxes are the grid cells the walls lie in, so the room's
// corners are corners of them however the walls meet. A
// hidden wall's face is left out, as its highlight would cover the view it
// was hidden to clear.
import * as THREE from 'three';
import type {ExteriorFace} from 'components/customer/RoomPlanner/helpers/componentGeometry';
import type {PlanAxis} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    getVisibleWalls,
    getWallSurfaceBox,
} from 'components/customer/RoomPlanner/helpers/wallProximity';
import type {SurfaceFacts} from 'components/customer/RoomPlanner/helpers/snapping/types';

/**
 * A wall of the room: its box, and whether it is shown. A hidden wall is the
 * one nearest the camera, cut away so the room can be seen into.
 */
export interface RoomWall {
    surface: THREE.Box3;
    visible: boolean;
}

// Wall edges this close (10 mm) are one line, so walls the room builds a few
// millimetres apart meet at one corner. Walls are far thicker.
const SAME_LINE = 2;

/**
 * @param {number[]} values Coordinates along one axis.
 * @return {Function} Each coordinate's line: the lowest of those within
 * SAME_LINE of each other in a run.
 */
const linesOf = (values: number[]): ((value: number) => number) => {
    const sorted = Array.from(new Set(values)).sort((a, b) => a - b);
    const line = new Map<number, number>();
    sorted.forEach((value, i) => {
        const previous = sorted[i - 1];
        line.set(
            value,
            i > 0 && value - previous <= SAME_LINE
                ? (line.get(previous) ?? value)
                : value
        );
    });

    return (value) => line.get(value) ?? value;
};

/**
 * @param {number} x Across the floor plan.
 * @param {number} z Along it.
 * @param {THREE.Box3[]} walls The walls' boxes.
 * @return {boolean} True when walls stand on all four sides of the point,
 * along x and z, as they do anywhere in the room.
 */
const enclosed = (x: number, z: number, walls: readonly THREE.Box3[]) => {
    const alongX = walls.filter(({min, max}) => min.z < z && z < max.z);
    const alongZ = walls.filter(({min, max}) => min.x < x && x < max.x);

    return (
        alongX.some(({max}) => max.x <= x) &&
        alongX.some(({min}) => min.x >= x) &&
        alongZ.some(({max}) => max.z <= z) &&
        alongZ.some(({min}) => min.z >= z)
    );
};

/**
 * One cell's edge where the room meets a wall: the face's axis, which way it
 * looks, where it lies, and its run along the other floor axis.
 */
interface Edge {
    axis: PlanAxis;
    side: -1 | 1;
    position: number;
    from: number;
    to: number;
}

/**
 * The floor plan cut into a grid at every wall's edges: each cell, the wall
 * it lies in, if any, and whether the room is there, closed in by walls.
 */
interface FloorGrid {
    xs: number[];
    zs: number[];
    cells: {wall: number; room: boolean}[][];
}

/**
 * @param {THREE.Box3[]} boxes The walls' boxes, on their lines.
 * @return {FloorGrid} The grid their edges cut the floor plan into.
 */
const floorGrid = (boxes: readonly THREE.Box3[]): FloorGrid => {
    const xs = Array.from(
        new Set(boxes.flatMap(({min, max}) => [min.x, max.x]))
    ).sort((a, b) => a - b);
    const zs = Array.from(
        new Set(boxes.flatMap(({min, max}) => [min.z, max.z]))
    ).sort((a, b) => a - b);
    const middle = (lines: number[], i: number) =>
        ((lines.at(i) ?? 0) + (lines.at(i + 1) ?? 0)) / 2;
    const cell = (i: number, j: number) => {
        const [x, z] = [middle(xs, i), middle(zs, j)];
        const wall = boxes.findIndex(
            ({min, max}) => min.x < x && x < max.x && min.z < z && z < max.z
        );

        return {wall, room: wall < 0 && enclosed(x, z, boxes)};
    };

    return {
        xs,
        zs,
        cells: xs
            .slice(0, -1)
            .map((_, i) => zs.slice(0, -1).map((__, j) => cell(i, j))),
    };
};

/**
 * The walls as solid boxes, one per grid cell a wall lies in, so every corner
 * of the room's outline, inside or out, is a corner of one, however the
 * walls meet.
 * @param {FloorGrid} grid The floor plan's grid.
 * @param {THREE.Box3[]} boxes The walls' boxes, for each cell's height.
 * @return {THREE.Box3[]} The boxes.
 */
const wallCells = (
    {xs, zs, cells}: FloorGrid,
    boxes: readonly THREE.Box3[]
): THREE.Box3[] =>
    cells.flatMap((column, i) =>
        column.flatMap(({wall}, j) => {
            const box = boxes.at(wall);
            if (wall < 0 || !box) return [];

            return [
                new THREE.Box3(
                    new THREE.Vector3(xs.at(i), box.min.y, zs.at(j)),
                    new THREE.Vector3(xs.at(i + 1), box.max.y, zs.at(j + 1))
                ),
            ];
        })
    );

/**
 * The room's interior faces: the edges where a cell the walls close in
 * meets a shown wall, merged along each run.
 * @param {FloorGrid} grid The floor plan's grid.
 * @param {boolean[]} visible Whether each wall is shown.
 * @param {THREE.Box3} bbox All the walls' box, for the faces' height.
 * @return {ExteriorFace[]} The faces.
 */
const interiorFaces = (
    {xs, zs, cells}: FloorGrid,
    visible: readonly boolean[],
    bbox: THREE.Box3
): ExteriorFace[] => {
    const at = (i: number, j: number) => cells.at(i)?.at(j);

    const edges: Edge[] = [];
    cells.forEach((column, i) =>
        column.forEach((here, j) => {
            if (!here.room) return;

            (
                [
                    ['x', -1, at(i - 1, j), xs.at(i), zs, j],
                    ['x', 1, at(i + 1, j), xs.at(i + 1), zs, j],
                    ['z', -1, at(i, j - 1), zs.at(j), xs, i],
                    ['z', 1, at(i, j + 1), zs.at(j + 1), xs, i],
                ] as const
            ).forEach(([axis, toward, next, position, run, k]) => {
                if (!next || next.wall < 0 || position === undefined) return;
                if (!visible.at(next.wall)) return;

                edges.push({
                    axis,
                    // The face looks back into the room, away from the wall.
                    side: toward < 0 ? 1 : -1,
                    position,
                    from: run.at(k) ?? 0,
                    to: run.at(k + 1) ?? 0,
                });
            });
        })
    );

    return mergeRuns(edges).map(({axis, side, position, from, to}) => {
        const rect =
            axis === 'x'
                ? new THREE.Box3(
                      new THREE.Vector3(position, bbox.min.y, from),
                      new THREE.Vector3(position, bbox.max.y, to)
                  )
                : new THREE.Box3(
                      new THREE.Vector3(from, bbox.min.y, position),
                      new THREE.Vector3(to, bbox.max.y, position)
                  );

        return {axis, side, position, rect};
    });
};

/**
 * @param {Edge[]} edges Cell edges.
 * @return {Edge[]} Those along one line, looking one way, joined where they
 * meet end to end.
 */
const mergeRuns = (edges: readonly Edge[]): Edge[] =>
    [...edges]
        .sort(
            (a, b) =>
                a.axis.localeCompare(b.axis) ||
                a.side - b.side ||
                a.position - b.position ||
                a.from - b.from
        )
        .reduce<Edge[]>((runs, edge) => {
            const last = runs.at(-1);
            if (
                last &&
                last.axis === edge.axis &&
                last.side === edge.side &&
                last.position === edge.position &&
                last.to === edge.from
            ) {
                last.to = edge.to;
            } else {
                runs.push({...edge});
            }

            return runs;
        }, []);

/**
 * The room's walls as something a product can meet: their interior faces,
 * and the grid cells they lie in as its solid boxes.
 */
export class WallFacts implements SurfaceFacts {
    readonly group: THREE.Group;
    readonly bbox: THREE.Box3;
    readonly solidBBoxes: readonly THREE.Box3[];
    readonly exteriorFaces: readonly ExteriorFace[];

    /**
     * @param {RoomWall[]} walls Every wall of the room, shown or not.
     */
    constructor(walls: readonly RoomWall[]) {
        const lineX = linesOf(
            walls.flatMap(({surface}) => [surface.min.x, surface.max.x])
        );
        const lineZ = linesOf(
            walls.flatMap(({surface}) => [surface.min.z, surface.max.z])
        );
        const boxes = walls.map(({surface: {min, max}}) => {
            const box = new THREE.Box3(
                new THREE.Vector3(lineX(min.x), min.y, lineZ(min.z)),
                new THREE.Vector3(lineX(max.x), max.y, lineZ(max.z))
            );

            return box;
        });

        this.group = new THREE.Group();
        this.group.name = 'roomWalls';
        this.group.userData.isRoomWall = true;
        this.bbox = boxes.reduce(
            (all, box) => all.union(box),
            new THREE.Box3()
        );
        const grid = floorGrid(boxes);
        this.solidBBoxes = wallCells(grid, boxes);
        this.exteriorFaces = interiorFaces(
            grid,
            walls.map(({visible}) => visible),
            this.bbox
        );
    }
}

// The walls described last for each wall group, and the walls they were
// described from: the same facts while the walls stay the same, so a corner
// or alignment held against them is kept from move to move.
const described = new WeakMap<
    THREE.Object3D,
    {key: string; facts: WallFacts}
>();

/**
 * @param {THREE.Object3D | null} [wallGroup] The room's wall group.
 * @return {WallFacts | null} Its walls, or null when it has none.
 */
export const roomWallsOf = (
    wallGroup?: THREE.Object3D | null
): WallFacts | null => {
    if (!wallGroup) return null;

    const meshes = wallGroup.children.filter(
        (child): child is THREE.Mesh =>
            (child as THREE.Mesh).isMesh && Boolean(child.name)
    );
    if (meshes.length === 0) return null;

    const shown = new Set(getVisibleWalls(wallGroup));
    const walls = meshes.map((mesh) => ({
        surface: getWallSurfaceBox(mesh),
        visible: shown.has(mesh),
    }));
    const key = walls
        .map(({surface: {min, max}, visible}) =>
            [...min.toArray(), ...max.toArray(), visible].join(',')
        )
        .join(';');
    const last = described.get(wallGroup);
    if (last?.key === key) return last.facts;

    const facts = new WallFacts(walls);
    described.set(wallGroup, {key, facts});

    return facts;
};
